/**
 * This file exposes frequently used lookup methods.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */

import { type COUNTRIES_CODE, COUNTRIES } from '~/constants/countries'
import { type REGIONS_CODE, PROVINCES, PROVINCES_FRENCH, REGIONS, STATES } from '~/constants/regions'

import { COUNTRY_CURRENCIES, COUNTRY_SHORTS } from '~/constants'

import type { Nullable } from '~/types'
import type { SupportedLanguages } from '~~/shared/types/locales'

type CountryIsoCodeForCurrency = keyof typeof COUNTRY_CURRENCIES

/**
 * Lookup a state or province name to its ISO code.
 *
 * This type uses a string because the values come from a JSON file which we're
 * not able to extract values as types from.
 *
 * @param stateName The name of the state or province.
 * @returns The ISO code of the state or province.
 *
 * @example lookupRegionToIsoCode('Alberta') // 'AB'
 */
export function lookupRegionToIsoCode(stateName?: Nullable<string>) {
  if (!stateName) {
    return
  }

  // Temporary French to English mapper, until we figure out how to deal with
  // states/provinces properly. For example: we should probably store the code
  // and do a reverse lookup on locale instead.
  const normalizedStateName = stateName in PROVINCES_FRENCH
    ? PROVINCES_FRENCH[stateName as keyof typeof PROVINCES_FRENCH]
    : stateName

  const isoCode = (Object.keys(REGIONS) as REGIONS_CODE[]).find((key) => REGIONS[key] === normalizedStateName)

  return isoCode
}

/**
 * Lookup a state or province ISO code to its name.
 *
 * @param regionIsoCode The ISO code of the state or province.
 * @returns The name of the state or province.
 *
 * @example lookupIsoCodeToRegion('AB') // 'Alberta'
 */
export function lookupIsoCodeToRegion(regionIsoCode: REGIONS_CODE) {
  return REGIONS[regionIsoCode]
}

/**
 * Lookup a country name to its ISO code.
 *
 * @param countryName The name of the country.
 * @returns The ISO code of the country.
 *
 * @example lookupCountryToIsoCode('Afghanistan') // 'AF'
 */
export function lookupCountryToIsoCode(countryName?: Nullable<string>) {
  if (!countryName) {
    return
  }

  const isoCode = (Object.keys(COUNTRIES) as COUNTRIES_CODE[]).find((key) => COUNTRIES[key] === countryName)
  return isoCode
}

/**
 * Lookup a country ISO code to its name.
 *
 * @param countryIsoCode The ISO code of the country.
 * @returns The name of the country.
 *
 * @example lookupIsoCodeToCountry('AF') // 'Afghanistan'
 */
export function lookupIsoCodeToCountry(countryIsoCode: COUNTRIES_CODE) {
  return COUNTRIES[countryIsoCode]
}

/**
 * Lookup a currency code for a given country code.
 *
 * @param countryCode The ISO code of the country.
 * @returns The currency code of the country.
 *
 * @example lookupCountryCodeToCurrencyCode('CA') // 'CAD'
 */
export function lookupCountryCodeToCurrencyCode(countryCode: CountryIsoCodeForCurrency) {
  return COUNTRY_CURRENCIES[countryCode]
}

/**
 * Lookup a US state or Canadian province ISO code to its country ISO code.
 *
 * @param regionCode The ISO code of the state or province.
 * @returns The ISO code of the country, or undefined if not recognized.
 *
 * @example lookupCountryCodeByRegionCode('ON') // 'CA'
 * @example lookupCountryCodeByRegionCode('TX') // 'US'
 */
export function lookupCountryCodeByRegionCode(regionCode?: Nullable<string>) {
  if (!regionCode) {
    return undefined
  }

  const code = regionCode.toUpperCase()

  if (code in PROVINCES) {
    return COUNTRY_SHORTS.CA
  }

  if (code in STATES) {
    return COUNTRY_SHORTS.US
  }

  return undefined
}

/**
 * Lookup a region type by country ISO code.
 *
 * @param countryIsoCode The ISO code of the country.
 * @returns The type of the region.
 *
 * @example lookupRegionTypeByCountryCode('CA') // 'province'
 */
export function lookupRegionTypeByCountryCode(countryIsoCode: COUNTRIES_CODE) {
  switch (countryIsoCode) {
    case 'CA':
      return 'province'

    case 'US':
      return 'state'

    default:
      return 'region'
  }
}

/**
 * Lookup a locale to its display language.
 *
 * @param locale The locale to lookup.
 * @returns The display language.
 *
 * @example lookupLocaleToLanguage('en') // 'English'
 */
export function lookupLocaleToLanguage(locale: SupportedLocales): SupportedLanguages {
  if (locale === 'fr') {
    return 'French'
  }

  return 'English'
}
